"use client";
import React, { useState } from "react";
import { Search, Eye, Edit2, Trash2, X, ChevronsLeft, ChevronsRight } from "lucide-react";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { useComplaintTypes } from "../../context/ComplaintTypeContext";

// Reusable input class so every field shares the same lavender focus style
const inputClass =
  "w-full border border-gray-300 rounded-md px-3 py-2 mt-1 outline-none transition-colors duration-150 focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff]/40";

export default function WorkplaceIssuesCategory() {
const {
  complaintTypes,
  createComplaintType,
  updateComplaintType,
  deleteComplaintType,
  loading,
  updateComplaintTypeStatus
} = useComplaintTypes();

const issues = complaintTypes || [];

  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
//   const [loading, setLoading] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Add modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [newIssue, setNewIssue] = useState({
  name:"",
  status:"active"
});
  const [errorMessage, setErrorMessage] = useState("");

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState(null);

  const statusColors = {
    active: "bg-green-100 text-green-600",
    inactive: "bg-red-100 text-red-600",
  };

  // ---------------------------
  // FILTER + SEARCH
  // ---------------------------
  const filteredIssues = (issues || []).filter((item) => {
    const matchesStatus =
      filter === "All" || item.status?.toLowerCase() === filter.toLowerCase();

    const matchesSearch = (item.type_name ?? "")
      .toLowerCase()
      .includes(search.toLowerCase());

    return matchesStatus && matchesSearch;
  });

  const totalPages = Math.ceil(filteredIssues.length / rowsPerPage) || 1;
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredIssues.slice(indexOfFirstRow, indexOfLastRow);

  const totalColumns = 4; // ID, Name, Status, Actions

  // ---------------------------
  // CRUD HANDLERS
  // ---------------------------
  const resetNewIssue = () => {
    setNewIssue({ name: "", status: "pending" });
    setErrorMessage("");
  };

const handleAddSubmit = async (e) => {
  e.preventDefault();

  if (!newIssue.name.trim()) {
    setErrorMessage("Name is required!");
    return;
  }

  setErrorMessage("");

  const user = JSON.parse(localStorage.getItem("user"));

  const result = await createComplaintType({
    type_name: newIssue.name.trim(),
    status: newIssue.status,
    create_audit_id: user?.id, // adjust key if your user id field is different
  });

  if (result.success) {
    setShowAddModal(false);
    resetNewIssue();
  } else {
    setErrorMessage(result.message);
  }
};

  const handleEdit = (item) => {
    setSelected({ ...item });
    setIsEditMode(true);
  };

  const handleView = (item) => {
    setSelected(item);
    setIsEditMode(false);
  };

const handleSave = async () => {
  if (!selected?.type_name?.trim()) {
    return;
  }

  const user = JSON.parse(localStorage.getItem("user"));

  const result = await updateComplaintType(
    selected.id,
    {
      type_name: selected.type_name.trim(),
      status: selected.status,
      update_audit_id: user?.id,
    }
  );

  if (result.success) {
    setSelected(null);
  }
};

  const confirmDelete = (item) => setDeleteTarget(item);

  const handleDelete = async () => {
  if (!deleteTarget) return;

  const user = JSON.parse(localStorage.getItem("user"));

  const result = await deleteComplaintType(
    deleteTarget.id,
    {
      delete_audit_id: user?.id, // change if your user id field is different
    }
  );

  if (result.success) {
    setDeleteTarget(null);
  }
};

const handleStatusToggle = async (item) => {
  const user = JSON.parse(localStorage.getItem("user"));

  const newStatus =
    item.status === "active" ? "inactive" : "active";

  const result = await updateComplaintTypeStatus(
    item.id,
    {
      status: newStatus,
      update_audit_id: user?.id,
    }
  );

  if (!result.success) {
    console.error(result.message);
  }
};


  const ReadOnlyField = ({ label, value }) => (
    <div>
      <label className="text-sm font-medium text-gray-600">{label}</label>
      <div className="w-full border rounded-md px-3 py-2 mt-1 bg-gray-50 text-gray-700 break-words">
        {value || value === 0 ? value : "—"}
      </div>
    </div>
  );

  return (
    <div className="bg-sky-50">
      <div className="px-4 sm:px-6 md:px-9 py-5">
        {/* Page header with category label */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-5">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">
            Workplace Issues Category
          </h1>
         
        </div>

        {/* Filter + Search */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6 bg-white rounded-xl p-3 shadow-sm gap-3">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full lg:w-auto">
            <div className="flex flex-col xs:flex-row sm:flex-row items-stretch border border-gray-300 rounded-md overflow-hidden w-full sm:w-auto transition-colors duration-150 focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
              {/* STATUS FILTER */}
              <select
                className="px-2 py-2 border-b sm:border-b-0 sm:border-r text-sm w-full sm:w-auto outline-none"
                value={filter}
                onChange={(e) => {
                  setFilter(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="All">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>

              {/* SEARCH */}
              <div className="relative w-full sm:w-40 md:w-56">
                <Search className="absolute left-2 top-2.5 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="pl-8 pr-2 py-2 w-full outline-none text-sm"
                />
              </div>
            </div>

            {(search || filter !== "All") && (
              <button
                type="button"
                onClick={() => {
                  setSearch("");
                  setFilter("All");
                  setCurrentPage(1);
                }}
                className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 transition whitespace-nowrap shrink-0 w-full sm:w-auto"
              >
                <X size={14} />
                Clear Filters
              </button>
            )}
          </div>

          {/* RIGHT BUTTON */}
          <button
            onClick={() => setShowAddModal(true)}
            className="text-white px-4 py-2 rounded-md shrink-0 w-full lg:w-auto"
            style={{ background: "linear-gradient(135deg, #2563eb, #d12465)" }}
          >
            + Add Issue
          </button>
        </div>

        {/* Table */}
        <div className="bg-white shadow rounded-xl p-3 sm:p-4">
          <div className="overflow-x-auto -mx-3 sm:mx-0">
            <table className="min-w-[500px] sm:min-w-full border-collapse text-xs sm:text-sm">
              <thead className="bg-sky-50">
                <tr>
                  <th className="py-3 px-2 sm:px-4 text-left font-bold text-gray-700 whitespace-nowrap">
                    ID
                  </th>
                  <th className="py-3 px-2 sm:px-4 text-left font-bold text-gray-700 whitespace-nowrap">
                    Name
                  </th>
                  <th className="py-3 px-2 sm:px-4 text-left font-bold text-gray-700 whitespace-nowrap">
                    Status
                  </th>
                  <th className="py-3 px-2 sm:px-4 text-center font-bold text-gray-700 whitespace-nowrap">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={totalColumns} className="text-center py-4 text-gray-500">
                      Loading...
                    </td>
                  </tr>
                ) : currentRows.length > 0 ? (
                  currentRows.map((item, index) => (
                    <tr
                      key={item.id}
                      className={`border-b hover:bg-gray-50 ${
                        index % 2 === 1 ? "bg-[#f7f6fe]" : "bg-white"
                      }`}
                    >
                      <td className="py-3 px-2 sm:px-4 text-[#8c57ff] whitespace-nowrap">
                        {(indexOfFirstRow + index + 1).toString().padStart(2, "0")}
                      </td>
                      <td className="py-3 px-2 sm:px-4 whitespace-nowrap">
                        {item.type_name}
                      </td>
                      <td className="py-3 px-2 sm:px-4 whitespace-nowrap">
  <button
    onClick={() => handleStatusToggle(item)}
    className={`px-3 py-1 rounded-full text-xs font-medium transition-all duration-200 ${
      item.status === "active"
        ? "bg-green-100 text-green-600"
        : "bg-red-100 text-red-600"
    }`}
  >
    {item.status === "active" ? "Active" : "Inactive"}
  </button>
</td>
                      <td className="py-3 px-2 sm:px-4">
                        <div className="flex justify-center items-center space-x-3 text-gray-600">
                          <Edit2
                            className="h-4 w-4 cursor-pointer hover:text-sky-600"
                            onClick={() => handleEdit(item)}
                          />
                          <Eye
                            className="h-4 w-4 cursor-pointer hover:text-sky-600"
                            onClick={() => handleView(item)}
                          />
                          <Trash2
                            className="h-4 w-4 cursor-pointer hover:text-red-600"
                            onClick={() => confirmDelete(item)}
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={totalColumns} className="text-center py-4 text-gray-500">
                      No records found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex flex-col sm:flex-row justify-between items-center mt-4 gap-3 text-sm">
            <div className="text-gray-500">
              Rows per page:
              <select
                className="border border-gray-300 rounded-md px-2 py-1 ml-1 outline-none transition-colors duration-150 focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff]/40"
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                {[5, 10, 15].map((num) => (
                  <option key={num} value={num}>
                    {num}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center flex-wrap justify-center gap-1">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronsLeft size={20} />
              </button>
              <button
                className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                <MdKeyboardArrowLeft size={20} />
              </button>

              {(() => {
                let startPage = Math.max(currentPage - 1, 1);
                let endPage = startPage + 2;
                if (endPage > totalPages) {
                  endPage = totalPages;
                  startPage = Math.max(endPage - 2, 1);
                }
                const pages = [];
                for (let i = startPage; i <= endPage; i++) {
                  pages.push(
                    <button
                      key={i}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        i === currentPage
                          ? "text-white bg-[linear-gradient(135deg,#2563eb,#d12465)]"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                      onClick={() => setCurrentPage(i)}
                    >
                      {i}
                    </button>
                  );
                }
                return pages;
              })()}

              <button
                className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                disabled={currentPage === totalPages}
              >
                <MdKeyboardArrowRight size={20} />
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronsRight size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Add Issue Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-3 sm:px-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-md p-4 sm:p-6 relative">
            <button
              onClick={() => {
                setShowAddModal(false);
                resetNewIssue();
              }}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-800"
            >
              <X className="h-5 w-5" />
            </button>

            <h2 className="text-xl font-semibold mb-4 pr-6">Add Workplace Issue</h2>

            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-600">Name</label>
                <input
                  type="text"
                  value={newIssue.name}
                  onChange={(e) =>
                    setNewIssue({ ...newIssue, name: e.target.value })
                  }
                  className={inputClass}
                  placeholder="e.g. Harassment Complaint"
                  disabled={loading}
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-600">Status</label>
                <select
                  value={newIssue.status}
                  onChange={(e) =>
                    setNewIssue({ ...newIssue, status: e.target.value })
                  }
                  className={inputClass}
                  disabled={loading}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              {errorMessage && (
                <div className="text-red-500 text-sm">{errorMessage}</div>
              )}

              <div className="mt-5 flex flex-col-reverse sm:flex-row justify-end gap-2 sm:space-x-2">
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    resetNewIssue();
                  }}
                  className="px-4 py-2 rounded-md border border-gray-300 hover:bg-gray-100"
                >
                  Cancel
                </button>

                <button
                  onClick={handleAddSubmit}
                  className="px-4 py-2 rounded-md text-white flex items-center justify-center"
                  disabled={loading}
                  style={{ background: "linear-gradient(135deg, #2563eb, #d12465)" }}
                >
                  {loading ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View / Edit Modal */}
      {selected && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-3 sm:px-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md relative">
            <div className="flex items-center justify-between px-4 sm:px-5 pt-4 sm:pt-5 pb-3 border-b">
              <h2 className="text-lg sm:text-xl font-semibold pr-6">
                {isEditMode ? "Edit Issue" : "View Issue"}
              </h2>
              <button
                onClick={() => setSelected(null)}
                className="text-gray-500 hover:text-gray-800"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="px-4 sm:px-5 py-4 space-y-3">
              {isEditMode ? (
                <div>
                  <label className="text-sm font-medium text-gray-600">Name</label>
                  <input
  type="text"
  className={inputClass}
  value={selected.type_name || ""}
  onChange={(e) =>
    setSelected({
      ...selected,
      type_name: e.target.value,
    })
  }
/>
                </div>
              ) : (
                <ReadOnlyField label="Name" value={selected.type_name} />
              )}

              {isEditMode ? (
                <div>
                  <label className="text-sm font-medium text-gray-600">Status</label>
                  <select
                    className={inputClass}
                    value={selected.status || ""}
                    onChange={(e) =>
                      setSelected({ ...selected, status: e.target.value })
                    }
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              ) : (
                <div>
                  <label className="text-sm font-medium text-gray-600">Status</label>
                  <div className="mt-1">
                    <span
                      className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                        statusColors[selected.status] || "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {selected.status || "—"}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {isEditMode && (
              <div className="px-4 sm:px-5 py-3 border-t flex justify-end">
                <button
  onClick={handleSave}
  disabled={loading}
  className="text-white px-4 py-2 rounded-md w-full sm:w-auto flex items-center justify-center gap-2 disabled:opacity-70"
  style={{ background: "linear-gradient(135deg, #2563eb, #d12465)" }}
>
  {loading ? (
    <>
      <svg
        className="animate-spin h-4 w-4 text-white"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
        />
      </svg>
      Updating...
    </>
  ) : (
    "Save"
  )}
</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-3 sm:px-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-sm p-5">
            <h2 className="text-lg font-semibold mb-2">Delete Issue</h2>
            <p className="text-sm text-gray-600 mb-5">
              Are you sure you want to delete{" "}
              <span className="font-medium text-gray-800">
                {deleteTarget.type_name}
              </span>
              ? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 rounded-md border border-gray-300 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 rounded-md text-white bg-red-500 hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}